import { z } from 'zod'

export const BUDGET_EXPENSE_STATUSES = ['planned', 'due', 'paid'] as const

export const budgetExpenseStatusSchema = z.enum(BUDGET_EXPENSE_STATUSES)

const optionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.string().trim().max(max).optional(),
  )

const clearableOptionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    z.string().trim().max(max).nullable().optional(),
  )

const optionalMoney = z
  .number()
  .int()
  .min(0, 'Amount cannot be negative')
  .max(100_000_000, 'Amount is too high')
  .optional()

const requiredMoney = z
  .number()
  .int()
  .min(0, 'Amount cannot be negative')
  .max(100_000_000, 'Amount is too high')

const optionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.iso.date().optional(),
)

const clearableOptionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.iso.date().nullable().optional(),
)

const colorSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex color')

export const createBudgetCategorySchema = z.object({
  name: z.string().trim().min(1, 'Enter category name').max(120),
  plannedAmount: requiredMoney,
  color: colorSchema.default('#c8506e'),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
})

export const updateBudgetCategorySchema = z.object({
  name: z.string().trim().min(1, 'Enter category name').max(120).optional(),
  plannedAmount: optionalMoney,
  color: colorSchema.optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
})

export const createBudgetExpenseSchema = z.object({
  categoryId: z.uuid(),
  title: z.string().trim().min(1, 'Enter expense title').max(160),
  vendorName: optionalText(160),
  amount: requiredMoney,
  status: budgetExpenseStatusSchema.default('planned'),
  dueDate: optionalDate,
  paidAt: optionalDate,
  notes: optionalText(2000),
})

export const updateBudgetExpenseSchema = z.object({
  categoryId: z.uuid().optional(),
  title: z.string().trim().min(1, 'Enter expense title').max(160).optional(),
  vendorName: clearableOptionalText(160),
  amount: optionalMoney,
  status: budgetExpenseStatusSchema.optional(),
  dueDate: clearableOptionalDate,
  paidAt: clearableOptionalDate,
  notes: clearableOptionalText(2000),
})

export type BudgetExpenseStatus = (typeof BUDGET_EXPENSE_STATUSES)[number]
export type CreateBudgetCategoryInput = z.infer<typeof createBudgetCategorySchema>
export type UpdateBudgetCategoryInput = z.infer<typeof updateBudgetCategorySchema>
export type CreateBudgetExpenseInput = z.infer<typeof createBudgetExpenseSchema>
export type UpdateBudgetExpenseInput = z.infer<typeof updateBudgetExpenseSchema>
